import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'

import { Observable, catchError, of, tap, throwError } from 'rxjs'

import { TranslateService } from '@ngx-translate/core'

import {
  LoginChallengeResponse,
  LoginRequest,
  LoginResponse,
  LoginSuccessResponse,
  LogoutResponse,
  MeResponse,
  PasswordChallengeRequest,
  ResetPasswordRequest,
  ResetPasswordResponse,
  isLoginChallengeResponse
} from './auth.types'
import { ChallengeStorageService } from './challenge-storage.service'
import { LocalePinService } from '../config/locale-pin.service'
import { AUTH_STORE } from '../tokens/auth-store.token'
import { ENVIRONMENT } from '../tokens/environment.token'

@Injectable({ providedIn: 'root' })
export class AuthService {
  private env = inject(ENVIRONMENT)
  private authStore = inject(AUTH_STORE)
  private http = inject(HttpClient)
  private translate = inject(TranslateService)
  private challengeStorage = inject(ChallengeStorageService)
  private localePin = inject(LocalePinService, { optional: true })
  private router = inject(Router)

  login(request: LoginRequest): Observable<LoginResponse> {
    this.authStore.setIsLoggingIn(true)

    return this.http
      .post<LoginResponse>(`${this.env.apiUrl}/auth/login/`, request, {
        withCredentials: true
      })
      .pipe(
        catchError((err) => {
          if (err?.error && isLoginChallengeResponse(err.error)) {
            return of(err.error as LoginChallengeResponse)
          }
          return throwError(() => err)
        }),
        tap({
          next: (response) => {
            if (!isLoginChallengeResponse(response)) {
              this.authStore.setUserFromLoginResponse(response)
              this.applyUserLanguage(response.language)
            }
            this.authStore.setIsLoggingIn(false)
          },
          error: () => {
            this.authStore.setIsLoggingIn(false)
          }
        })
      )
  }

  completePasswordChallenge(request: PasswordChallengeRequest): Observable<LoginSuccessResponse> {
    return this.http
      .post<LoginSuccessResponse>(`${this.env.apiUrl}/auth/challenge/new-password/`, request, {
        withCredentials: true
      })
      .pipe(
        tap((response) => {
          this.challengeStorage.clearChallenge()
          this.authStore.setUserFromLoginResponse(response)
          this.applyUserLanguage(response.language)
        })
      )
  }

  logout(): Observable<LogoutResponse> {
    return this.http
      .post<LogoutResponse>(
        `${this.env.apiUrl}/auth/logout/`,
        {},
        {
          withCredentials: true
        }
      )
      .pipe(
        tap(() => {
          this.authStore.clearAuth()
        })
      )
  }

  /**
   * End a session the server no longer honours (refresh rejected, socket closed
   * as session gone): clear local auth state and go to login without waiting on
   * the server, then blacklist the refresh token best-effort.
   */
  endSession(): void {
    this.authStore.clearAuth()
    this.router.navigate(['/login'])
    this.logout().subscribe({ error: () => {} })
  }

  fetchCurrentUser(): Observable<MeResponse> {
    return this.http.get<MeResponse>(`${this.env.apiUrl}/users/me/`, {
      withCredentials: true
    })
  }

  refreshAccessToken(): Observable<void> {
    return this.http.post<void>(
      `${this.env.apiUrl}/auth/refresh/`,
      {},
      {
        withCredentials: true
      }
    )
  }

  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(
      `${this.env.apiUrl}/auth/reset-password/`,
      request,
      { withCredentials: true }
    )
  }

  private applyUserLanguage(language: string | null): void {
    // A pinned language (a site's, a kiosk's) outranks the account's, so a
    // re-login inside a pinned session must not quietly undo it.
    if (this.localePin?.currentPin) return

    const userLang = language?.split('-')[0]
    const currentLang = this.translate.getCurrentLang()

    if (userLang && userLang !== currentLang) {
      this.translate.use(userLang)
    }
  }
}
